/**
 * String manipulation utilities
 */

type PluralRules = { [key: string]: string };

/**
 * Converts a string to title case with proper handling of minor words
 */
export const titleCase = (str: string): string => {
  let result = str.replace(/([^\W_]+[^\s-]*) */g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });

  // Certain minor words should be left lowercase unless
  // they are the first or last words in the string
  const lowers = [
    'A',
    'An',
    'The',
    'And',
    'But',
    'Or',
    'For',
    'Nor',
    'As',
    'At',
    'By',
    'For',
    'From',
    'In',
    'Into',
    'Near',
    'Of',
    'On',
    'Onto',
    'To',
    'With',
  ];

  for (let i = 0; i < lowers.length; i++) {
    result = result.replace(new RegExp('\\s' + lowers[i] + '\\s', 'g'), (txt) => {
      return txt.toLowerCase();
    });
  }

  // Certain words such as initialisms or acronyms should be left uppercase
  const uppers = ['Id', 'Tv'];
  for (let i = 0; i < uppers.length; i++) {
    result = result.replace(new RegExp('\\b' + uppers[i] + '\\b', 'g'), uppers[i].toUpperCase());
  }

  return result;
};

/**
 * Converts a string to a URL-friendly slug
 */
export const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

/**
 * Gets a value from a nested object using dot notation
 * If the value is null, returns empty string
 */
export const byString = (o: object | null | undefined, s: string): unknown => {
  if (typeof s !== 'string') return '';

  const arr = s.split('.');
  let current: unknown = o;

  for (const key of arr) {
    if (current == null) break;
    current = (current as Record<string, unknown>)[key];
  }

  if (current === null) return '';

  if (typeof current === 'object' && current !== null && 'unit' in current) {
    const typed = current as { value?: unknown; unit?: unknown };
    if (typed.unit && typed.value !== undefined && typed.value !== null) {
      return typed.value + ' ' + typed.unit;
    }
  }

  return current;
};

/**
 * Converts a string to its plural form
 */
export const pluralize = (str: string): string => {
  const revert = false;

  const plural: PluralRules = {
    '(quiz)$': '$1zes',
    '^(ox)$': '$1en',
    '([m|l])ouse$': '$1ice',
    '(matr|vert|ind)ix|ex$': '$1ices',
    '(x|ch|ss|sh)$': '$1es',
    '([^aeiouy]|qu)y$': '$1ies',
    '(hive)$': '$1s',
    '(?:([^f])fe|([lr])f)$': '$1$2ves',
    '(shea|lea|loa|thie)f$': '$1ves',
    sis$: 'ses',
    '([ti])um$': '$1a',
    '(tomat|potat|ech|her|vet)o$': '$1oes',
    '(bu)s$': '$1ses',
    '(alias)$': '$1es',
    '(octop)us$': '$1i',
    '(ax|test)is$': '$1es',
    '(us)$': '$1es',
    '([^s]+)$': '$1s',
  };

  const singular: PluralRules = {
    '(quiz)zes$': '$1',
    '(matr)ices$': '$1ix',
    '(vert|ind)ices$': '$1ex',
    '^(ox)en$': '$1',
    '(alias)es$': '$1',
    '(octop|vir)i$': '$1us',
    '(cris|ax|test)es$': '$1is',
    '(shoe)s$': '$1',
    '(o)es$': '$1',
    '(bus)es$': '$1',
    '([m|l])ice$': '$1ouse',
    '(x|ch|ss|sh)es$': '$1',
    '(m)ovies$': '$1ovie',
    '(s)eries$': '$1eries',
    '([^aeiouy]|qu)ies$': '$1y',
    '([lr])ves$': '$1f',
    '(tive)s$': '$1',
    '(hive)s$': '$1',
    '(li|wi|kni)ves$': '$1fe',
    '(shea|loa|lea|thie)ves$': '$1f',
    '(^analy)ses$': '$1sis',
    '((a)naly|(b)a|(d)iagno|(p)arenthe|(p)rogno|(s)ynop|(t)he)ses$': '$1$2sis',
    '([ti])a$': '$1um',
    '(n)ews$': '$1ews',
    '(h|bl)ouses$': '$1ouse',
    '(corpse)s$': '$1',
    '(us)es$': '$1',
    s$: '',
  };

  const irregular: PluralRules = {
    move: 'moves',
    foot: 'feet',
    goose: 'geese',
    sex: 'sexes',
    child: 'children',
    man: 'men',
    tooth: 'teeth',
    person: 'people',
  };

  const uncountable = ['sheep', 'fish', 'deer', 'moose', 'series', 'species', 'money', 'rice', 'information', 'equipment'];

  // save some time in the case that singular and plural are the same
  if (uncountable.indexOf(str.toLowerCase()) >= 0) return str;

  // check for irregular forms
  for (const word in irregular) {
    let pattern: RegExp;
    let replace: string;

    if (revert) {
      pattern = new RegExp(irregular[word] + '$', 'i');
      replace = word;
    } else {
      pattern = new RegExp(word + '$', 'i');
      replace = irregular[word];
    }
    if (pattern.test(str)) return str.replace(pattern, replace);
  }

  const array = revert ? singular : plural;

  // check for matches using regular expressions
  for (const reg in array) {
    const pattern = new RegExp(reg, 'i');

    if (pattern.test(str)) return str.replace(pattern, array[reg]);
  }

  return str;
};
